import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const STATUS = {
  pending: { label: 'Ожидает подписания', color: '#f59e0b', bg: '#fef3c7' },
  signed:  { label: 'Подписал',           color: '#16a34a', bg: '#dcfce7' },
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function buildLink(client) {
  if (!client.payments || !client.payments.length) return 'https://sign.ucfemida.ru';
  const pays = client.payments.map(p => `${p.amount}:${p.date}`).join(',');
  return `https://sign.ucfemida.ru?amount=${client.total_amount}&payments=${pays}`;
}

const emptyForm = { name: '', phone: '', email: '', total_amount: '', payments: [] };

export default function RemoteClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [copied, setCopied] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/remote-clients').then(r => { setClients(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function openAdd() { setEditing(null); setForm(emptyForm); setShowForm(true); }
  function openEdit(c) {
    setEditing(c.id);
    setForm({ name: c.name, phone: c.phone || '', email: c.email || '', total_amount: c.total_amount || '', payments: c.payments || [] });
    setShowForm(true);
  }
  function closeForm() { setShowForm(false); setEditing(null); }

  function addPayment() { setForm(f => ({ ...f, payments: [...f.payments, { amount: '', date: '' }] })); }
  function removePayment(i) { setForm(f => ({ ...f, payments: f.payments.filter((_, idx) => idx !== i) })); }
  function updatePayment(i, field, val) {
    setForm(f => { const p = [...f.payments]; p[i] = { ...p[i], [field]: val }; return { ...f, payments: p }; });
  }

  async function save() {
    if (!form.name.trim()) return;
    setSaving(true);
    const body = { ...form, total_amount: parseInt(form.total_amount) || 0, payments: form.payments.filter(p => p.amount && p.date) };
    try {
      if (editing) {
        await api.put(`/remote-clients/${editing}`, body);
      } else {
        await api.post('/remote-clients', body);
      }
      closeForm();
      load();
    } catch (e) { alert('Ошибка: ' + (e.response?.data?.error || e.message)); }
    setSaving(false);
  }

  async function del(id) {
    if (!window.confirm('Удалить клиента?')) return;
    await api.delete(`/remote-clients/${id}`);
    load();
  }

  function copyLink(client) {
    const link = buildLink(client);
    navigator.clipboard.writeText(link).then(() => {
      setCopied(client.id);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b' }}>Дистанционные клиенты</h2>
        <button onClick={openAdd} style={btnStyle}>+ Добавить клиента</button>
      </div>

      {loading ? <div style={{ color: '#94a3b8' }}>Загрузка...</div> : (
        clients.length === 0 ? (
          <div style={{ color: '#94a3b8', textAlign: 'center', padding: 48 }}>Нет клиентов. Добавьте первого.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {clients.map(c => {
              const st = STATUS[c.status] || STATUS.pending;
              return (
                <div key={c.id} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <span style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{c.name}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: st.bg, color: st.color }}>{st.label}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 20, fontSize: 13, color: '#64748b', flexWrap: 'wrap' }}>
                        {c.phone && <span>📞 {c.phone}</span>}
                        {c.email && <span>✉️ {c.email}</span>}
                        {c.total_amount > 0 && <span>💰 {c.total_amount.toLocaleString('ru-RU')} ₽</span>}
                        {c.signed_at && <span>✅ Подписал {formatDate(c.signed_at)}</span>}
                      </div>
                      {c.payments && c.payments.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {c.payments.map((p, i) => (
                            <span key={i} style={{ fontSize: 12, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 8px', color: '#475569' }}>
                              Платёж {i + 1}: {parseInt(p.amount).toLocaleString('ru-RU')} ₽ до {p.date}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                      <button onClick={() => copyLink(c)} style={{ ...actionBtn, background: copied === c.id ? '#dcfce7' : '#eff6ff', color: copied === c.id ? '#16a34a' : '#2563eb' }}>
                        {copied === c.id ? '✓ Скопировано' : '🔗 Копировать ссылку'}
                      </button>
                      <button onClick={() => openEdit(c)} style={{ ...actionBtn, background: '#f8fafc', color: '#475569' }}>✏️ Изменить</button>
                      <button onClick={() => del(c.id)} style={{ ...actionBtn, background: '#fef2f2', color: '#dc2626' }}>🗑 Удалить</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {showForm && (
        <div style={overlayStyle} onClick={e => e.target === e.currentTarget && closeForm()}>
          <div style={modalStyle}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: '#1e293b' }}>
              {editing ? 'Редактировать клиента' : 'Новый клиент'}
            </h3>

            <label style={labelStyle}>ФИО *</label>
            <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Палеха Виктория Максимовна" />

            <label style={labelStyle}>Телефон</label>
            <input style={inputStyle} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+7 (999) 000-00-00" />

            <label style={labelStyle}>Email</label>
            <input style={inputStyle} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="client@mail.ru" />

            <label style={labelStyle}>Общая сумма (₽)</label>
            <input style={inputStyle} type="number" value={form.total_amount} onChange={e => setForm(f => ({ ...f, total_amount: e.target.value }))} placeholder="30000" />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, marginBottom: 10 }}>
              <label style={{ ...labelStyle, margin: 0, fontWeight: 600 }}>График платежей</label>
              <button onClick={addPayment} style={{ fontSize: 13, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Добавить платёж</button>
            </div>
            {form.payments.map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input style={{ ...inputStyle, flex: 1, margin: 0 }} type="number" placeholder="Сумма" value={p.amount} onChange={e => updatePayment(i, 'amount', e.target.value)} />
                <input style={{ ...inputStyle, flex: 1, margin: 0 }} type="text" placeholder="15.06.2026" value={p.date} onChange={e => updatePayment(i, 'date', e.target.value)} />
                <button onClick={() => removePayment(i)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}>×</button>
              </div>
            ))}

            {form.payments.filter(p => p.amount && p.date).length > 0 && (
              <div style={{ marginTop: 8, padding: '10px 12px', background: '#eff6ff', borderRadius: 8, fontSize: 12, color: '#1e40af', wordBreak: 'break-all' }}>
                🔗 {buildLink({ total_amount: parseInt(form.total_amount) || 0, payments: form.payments })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button onClick={save} disabled={saving || !form.name.trim()} style={{ ...btnStyle, flex: 1, opacity: !form.name.trim() ? 0.5 : 1 }}>
                {saving ? 'Сохраняю...' : 'Сохранить'}
              </button>
              <button onClick={closeForm} style={{ ...btnStyle, flex: 1, background: '#f1f5f9', color: '#475569' }}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const cardStyle = { background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' };
const btnStyle = { padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const actionBtn = { padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modalStyle = { background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6, marginTop: 14 };
const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, fontFamily: 'inherit', outline: 'none', marginTop: 0 };
